// ============================================
// DOCUMENT FILTERING UTILITIES - PATH B
// Filters PDFs without text extraction
// ============================================

import * as pdfjsLib from 'pdfjs-dist';

// ✅ CORRECT: Point to the worker file in public folder
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Classify document based on filename and quick text check
 * Returns: { type: string, shouldInclude: boolean, confidence: string }
 */
async function classifyDocument(file) {
  const lowerFilename = file.name.toLowerCase();
  
  // Quick classification based on filename patterns
  // Tax forms - INCLUDE
  if (lowerFilename.includes('1040') || lowerFilename.includes('form1040')) {
    return { type: 'form_1040', shouldInclude: true, confidence: 'high' };
  }
  
  if (lowerFilename.includes('w-2') || lowerFilename.includes('w2')) {
    return { type: 'w2', shouldInclude: true, confidence: 'high' };
  }
  
  if (lowerFilename.includes('schedule')) {
    return { type: 'schedule', shouldInclude: true, confidence: 'medium' };
  }
  
  if (lowerFilename.includes('1099')) {
    return { type: '1099', shouldInclude: true, confidence: 'high' };
  }
  
  if (lowerFilename.includes('k-1') || lowerFilename.includes('k1')) {
    return { type: 'schedule_k1', shouldInclude: true, confidence: 'high' };
  }

  // Paystubs are a supported document type (the backend has a paystub_extraction
  // feature and a /api/extract-paystub endpoint), but they were never classified
  // here — every paystub fell through to 'unknown' and was included with a warning.
  if (lowerFilename.includes('paystub') || lowerFilename.includes('pay-stub') ||
      lowerFilename.includes('pay_stub') || lowerFilename.includes('payslip') ||
      lowerFilename.includes('earnings')) {
    return { type: 'paystub', shouldInclude: true, confidence: 'high' };
  }
  
  // Non-tax documents - EXCLUDE
  if (lowerFilename.includes('bank') || 
      lowerFilename.includes('statement') ||
      lowerFilename.includes('checking') ||
      lowerFilename.includes('savings')) {
    return { type: 'bank_statement', shouldInclude: false, confidence: 'high' };
  }
  
  if (lowerFilename.includes('engagement') || 
      lowerFilename.includes('letter') ||
      lowerFilename.includes('cover')) {
    return { type: 'engagement_letter', shouldInclude: false, confidence: 'medium' };
  }
  
  // If we can't tell from filename, do a quick text check
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    // Just check first page
    const page = await pdf.getPage(1);
    const textContent = await page.getTextContent();
    const text = textContent.items.map(item => item.str).join(' ').toLowerCase();
    
    // Check for tax form indicators
    if (text.includes('form 1040') || text.includes('u.s. individual income tax')) {
      return { type: 'form_1040', shouldInclude: true, confidence: 'high' };
    }
    
    if (text.includes('schedule c') || text.includes('schedule e')) {
      return { type: 'schedule', shouldInclude: true, confidence: 'high' };
    }
    
    if (text.includes('form w-2') || text.includes('wage and tax statement')) {
      return { type: 'w2', shouldInclude: true, confidence: 'high' };
    }

    // Paystub content markers — 'year to date' / 'ytd' plus pay-period language
    if ((text.includes('year to date') || text.includes('ytd')) &&
        (text.includes('gross pay') || text.includes('net pay') ||
         text.includes('pay period') || text.includes('earnings statement'))) {
      return { type: 'paystub', shouldInclude: true, confidence: 'high' };
    }
    
    // Check for non-tax indicators
    if (text.includes('bank statement') || text.includes('account summary')) {
      return { type: 'bank_statement', shouldInclude: false, confidence: 'high' };
    }
    
  } catch (error) {
    console.warn('Could not check PDF content:', error);
  }
  
  // Default: INCLUDE (conservative - don't accidentally skip tax forms)
  console.warn('Unknown document type, including to be safe:', file.name);
  return { type: 'unknown', shouldInclude: true, confidence: 'low' };
}

/**
 * Process uploaded files - filter PDFs without text extraction
 * Returns: { relevantFiles, skippedFiles, stats }
 */
export async function processUploadedFiles(files) {
  const results = {
    relevantFiles: [],
    skippedFiles: [],
    stats: {
      total: files.length,
      processed: 0,
      included: 0,
      excluded: 0,
      failed: 0
    }
  };
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      console.log(`📄 Classifying ${i + 1}/${files.length}: ${file.name}`);
      
      // Classify the document
      const classification = await classifyDocument(file);
      
      results.stats.processed++;
      
      if (classification.shouldInclude) {
        // Keep the original PDF file
        results.relevantFiles.push(file);
        results.stats.included++;
        console.log(`✅ Included: ${file.name} (${classification.type})`);
      } else {
        results.skippedFiles.push({
          filename: file.name,
          type: classification.type,
          reason: 'Not a tax form'
        });
        results.stats.excluded++;
        console.log(`⏭️  Skipped: ${file.name} (${classification.type})`);
      }
      
    } catch (error) {
      console.error(`❌ Failed to process ${file.name}:`, error);
      results.stats.failed++;
      // On error, include the file (don't risk excluding a tax form)
      results.relevantFiles.push(file);
      results.stats.included++;
    }
  }
  
  console.log('📊 Classification complete:', results.stats);
  
  return results;
}
